import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { TOKEN_KEY } from '../config/api';
import { needsSignupResume } from '../utils/needsSignupResume';

interface RoleProtectedRouteProps {
  children: React.ReactNode;
  requiredRole: 'admin' | 'doctor' | 'user';
}

const RoleProtectedRoute = ({
  children,
  requiredRole,
}: RoleProtectedRouteProps) => {
  const { isLoading, user, getRoleBasedRedirect } = useAuth();
  const token = localStorage.getItem(TOKEN_KEY);
  const hasToken = !!token;

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#F8F7EC] to-[#E8F1D1] flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#7E8D4C] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[#6b6358]">Loading...</p>
        </div>
      </div>
    );
  }

  if (!hasToken) {
    return <Navigate to="/login" replace />;
  }

  // Same rules as OnboardingGate / ProtectedRoute (doctor level 3, patient level 5).
  if (needsSignupResume(user)) {
    return <Navigate to="/signup" replace />;
  }

  // Check if user has required role
  const userRole = user?.role?.toLowerCase();

  // Explicit "patient" is a real switch into the member view. A missing flag on a
  // doctor account is only the gap before profile lands — keep them on doctor pages.
  const activeProfile = (user?.active_profile_type ?? '').toLowerCase();

  const hasDoctorRouteAccess =
    activeProfile === 'doctor' ||
    (userRole === 'doctor' && activeProfile === '');

  const hasAccess =
    (requiredRole === 'admin' && userRole === 'admin') ||
    (requiredRole === 'doctor' && hasDoctorRouteAccess) ||
    (requiredRole === 'user' &&
      (activeProfile === 'patient' || userRole === 'user'));

  if (!hasAccess) {
    const redirectPath = getRoleBasedRedirect(
      user?.role,
      user?.active_profile_type,
    );
    return <Navigate to={redirectPath} replace />;
  }

  return <>{children}</>;
};

export default RoleProtectedRoute;
